/**
 * Sidebar navigation component.
 *
 * Sprint UX-1 (2026-05-19): retired the umbrella "MODULES" section. Each
 * module renders as its own top-level collapsible section (styled like
 * the Administration section). The previous "Intranet Sites" group is
 * folded into the CONTENT MANAGEMENT section as its first child group,
 * above the CM module's own pages (Sites / Templates / Search).
 *
 * Items still come from `/v1/settings/navigation` filtered server-side
 * by RBAC; an additional client-side check is retained as defence in
 * depth. On mobile (≤768px) the sidebar renders as a slide-over overlay.
 *
 * Cross-References:
 *   - Style Schema §2.2: Sidebar (260px, fixed, --color-bg-secondary)
 *   - FR-UI-001: Left sidebar with dynamic nav items
 *   - FR-UI-006: Mobile responsive behavior
 *   - S-MOD.11: Frontend dynamic navigation
 *   - sprint-ux-1-sidebar-module-sections.md
 */
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronDown, ChevronRight, Globe, ExternalLink, Settings } from 'lucide-react';
import { useUiStore } from '@/stores/uiStore';
import { useAuthStore } from '@/stores/authStore';
import { useMediaQuery, BREAKPOINTS } from '@/hooks/useMediaQuery';
import { useNavigation, type NavItem } from '@/hooks/useNavigation';
import { useIntranetSites } from '@/hooks/useIntranetSites';
import ExternalSiteIcon from '@/components/shell/ExternalSiteIcon';
import { resolveIcon } from '@/lib/iconMap';
import { apiFetch } from '@/lib/api';
import { lazy, Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { rememberNavScroll, restoreNavScroll, revealCurrentLink } from './sidebarScroll';

const MainNavSettingsModal = lazy(
  () => import('@/components/navigation/MainNavSettingsModal'),
);
const PageSettingsModal = lazy(
  () => import('@/modules/cm/components/PageSettingsModal'),
);
import SiteNavTree from './SiteNavTree';

const SITE_EXPAND_STORAGE_KEY = 'cm-sidebar-site-expanded';
const SECTION_EXPAND_STORAGE_KEY = 'sidebar-section-expanded';

const CM_MODULE_NAME = 'Content Management';
const ADMIN_SECTION_KEY = '__admin__';

/**
 * Sprint NAV-2 — Administration sub-group display order. Groups not
 * listed here fall to the bottom under "Other" (so we never silently
 * drop a row whose admin_group hasn't been registered).
 */
const ADMIN_GROUP_ORDER: readonly string[] = [
  'Identity',
  'Tenant',
  'Observability',
  'Content Management',
  'Connected Workspace',
  'Employee Lifecycle',
  'Platform',
  'Other',
];
const ADMIN_GROUP_DEFAULT_EXPANDED: ReadonlySet<string> = new Set(['Identity']);

function readSiteExpanded(): Set<string> {
  try {
    const raw = localStorage.getItem(SITE_EXPAND_STORAGE_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? new Set(arr) : new Set();
  } catch {
    return new Set();
  }
}

function writeSiteExpanded(expanded: Set<string>) {
  try {
    localStorage.setItem(SITE_EXPAND_STORAGE_KEY, JSON.stringify([...expanded]));
  } catch {
    /* localStorage unavailable */
  }
}

function readSectionExpanded(): Set<string> {
  try {
    const raw = localStorage.getItem(SECTION_EXPAND_STORAGE_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? new Set(arr) : new Set();
  } catch {
    return new Set();
  }
}

function writeSectionExpanded(expanded: Set<string>) {
  try {
    localStorage.setItem(SECTION_EXPAND_STORAGE_KEY, JSON.stringify([...expanded]));
  } catch {
    /* localStorage unavailable */
  }
}

/* ── Loading skeleton ──────────────────────────────────────── */

function NavSkeleton() {
  return (
    <div className="sidebar__skeleton" aria-hidden="true">
      {Array.from({ length: 6 }, (_, i) => (
        <div key={i} className="sidebar__skeleton-item">
          <div className="sidebar__skeleton-icon" />
          <div className="sidebar__skeleton-text" />
        </div>
      ))}
    </div>
  );
}

/* ── Module grouping ──────────────────────────────────────── */

interface ModuleSection {
  name: string;       // e.g. "Content Management"
  rank: number;       // min(sort_order) across the section's items
  items: NavItem[];   // sorted by sort_order ascending
}

/** Sprint NAV-2 — Administration sub-grouping. Buckets `section==='admin'`
 *  items by their `admin_group` (falling back to `'Other'` for rows the
 *  bootstrap left untagged), then orders the buckets by the locked
 *  `ADMIN_GROUP_ORDER` constant. Items inside each bucket stay in
 *  `sort_order` ascending. */
interface AdminGroup {
  name: string;
  items: NavItem[];
}

function computeAdminGroups(items: NavItem[]): AdminGroup[] {
  const map = new Map<string, NavItem[]>();
  for (const item of items) {
    if (item.section !== 'admin') continue;
    const key = item.admin_group ?? 'Other';
    const arr = map.get(key);
    if (arr) arr.push(item);
    else map.set(key, [item]);
  }
  const orderIndex = new Map(
    ADMIN_GROUP_ORDER.map((name, idx) => [name, idx] as const),
  );
  const out: AdminGroup[] = [];
  for (const [name, group] of map) {
    group.sort((a, b) => a.sort_order - b.sort_order || a.label.localeCompare(b.label));
    out.push({ name, items: group });
  }
  out.sort((a, b) => {
    const ai = orderIndex.get(a.name) ?? ADMIN_GROUP_ORDER.length;
    const bi = orderIndex.get(b.name) ?? ADMIN_GROUP_ORDER.length;
    return ai - bi || a.name.localeCompare(b.name);
  });
  return out;
}

/** Group `section==="modules"` items by module_name, ranking each
 *  section by the lowest sort_order across its items. Items inside
 *  each section stay in sort_order ascending. */
function computeModuleSections(items: NavItem[]): ModuleSection[] {
  const map = new Map<string, NavItem[]>();
  for (const item of items) {
    if (item.section !== 'modules') continue;
    const key = item.module_name ?? 'Other';
    const arr = map.get(key);
    if (arr) arr.push(item);
    else map.set(key, [item]);
  }
  const out: ModuleSection[] = [];
  for (const [name, group] of map) {
    group.sort((a, b) => a.sort_order - b.sort_order);
    const rank = group.length > 0 ? group[0]!.sort_order : 99;
    out.push({ name, rank, items: group });
  }
  out.sort((a, b) => a.rank - b.rank || a.name.localeCompare(b.name));
  return out;
}

/** Sprint NAV-3 — bucket a module section's items into loose
 *  (no `sub_group`) and grouped (`sub_group` set). Loose render
 *  first; sub-groups follow in `min(sort_order)` ascending. Items
 *  inside each bucket keep their `sort_order` ascending — caller
 *  has already sorted, so we preserve that. */
interface ModuleSubGroup {
  name: string;
  rank: number;
  items: NavItem[];
}

interface ModuleLayout {
  loose: NavItem[];
  subGroups: ModuleSubGroup[];
}

function computeModuleLayout(items: NavItem[]): ModuleLayout {
  const loose: NavItem[] = [];
  const map = new Map<string, NavItem[]>();
  for (const item of items) {
    if (!item.sub_group) {
      loose.push(item);
      continue;
    }
    const arr = map.get(item.sub_group);
    if (arr) arr.push(item);
    else map.set(item.sub_group, [item]);
  }
  const subGroups: ModuleSubGroup[] = [];
  for (const [name, group] of map) {
    const rank = group.length > 0 ? group[0]!.sort_order : 99;
    subGroups.push({ name, rank, items: group });
  }
  subGroups.sort((a, b) => a.rank - b.rank || a.name.localeCompare(b.name));
  return { loose, subGroups };
}

/* ── Component ─────────────────────────────────────────────── */

export function Sidebar() {
  const { sidebarCollapsed, mobileSidebarOpen, setMobileSidebarOpen } = useUiStore();
  const permissions = useAuthStore((s) => s.user?.permissions ?? []);
  const isMobile = useMediaQuery(BREAKPOINTS.mobile);
  const location = useLocation();

  const [expandedSections, setExpandedSections] = useState<Set<string>>(() =>
    readSectionExpanded(),
  );
  // Sprint CM-15 TKT-4 — local site order during drag (optimistic);
  // the actual persistence call fires onDrop. We start with whatever
  // useIntranetSites returns (server-sorted) and re-sync on refetch.
  const [siteDragOrder, setSiteDragOrder] = useState<string[] | null>(null);
  const [siteDraggingId, setSiteDraggingId] = useState<string | null>(null);
  // Sprint CM-11 — per-site expand state for the page tree.
  const [expandedSites, setExpandedSites] = useState<Set<string>>(() =>
    readSiteExpanded(),
  );

  const toggleSiteExpand = (siteId: string) => {
    setExpandedSites((prev) => {
      const next = new Set(prev);
      if (next.has(siteId)) next.delete(siteId);
      else next.add(siteId);
      writeSiteExpanded(next);
      return next;
    });
  };

  // CM-17-W2 — PageSettingsModal driven by the section-header gear
  // and the per-root-page hover gears.
  // CM-17-W3 — gate the gears behind `pages.update.org` (the org-scoped
  // permission that grants reorganize/rename/delete). The endpoints
  // themselves already enforce the same; the gate is UI defence-in-depth
  // so non-editors don't see options they can't use.
  const canManageCm = permissions.includes('pages.update.org')
    || permissions.includes('cm.admin');
  const [cmModal, setCmModal] = useState<{
    open: boolean;
    initialPageId: string | null;
    initialTab: 'pages' | 'settings';
  }>({ open: false, initialPageId: null, initialTab: 'pages' });
  const openCmModalPages = useCallback(() => {
    setCmModal({ open: true, initialPageId: null, initialTab: 'pages' });
  }, []);
  const openCmModalSettings = useCallback((pageId: string) => {
    setCmModal({ open: true, initialPageId: pageId, initialTab: 'settings' });
  }, []);
  const closeCmModal = useCallback(() => {
    setCmModal((prev) => ({ ...prev, open: false }));
  }, []);

  const toggleSection = useCallback((key: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      writeSectionExpanded(next);
      return next;
    });
  }, []);

  const { items, loading, error, refetch } = useNavigation();
  const { sites: intranetSites } = useIntranetSites();
  // The menu refetches on every route change; swapping it for the
  // skeleton then would drop the nav's scroll position to the top.
  const showSkeleton = loading && items.length === 0;

  // Keep the nav where the user left it across page loads, and the
  // current page's link in view (also when the mobile menu opens).
  const navRef = useRef<HTMLElement>(null);
  const scrollRestored = useRef(false);
  useLayoutEffect(() => {
    const nav = navRef.current;
    if (!nav || showSkeleton) return;
    if (!scrollRestored.current) {
      restoreNavScroll(nav);
      scrollRestored.current = true;
    }
    revealCurrentLink(nav);
  }, [location.pathname, showSkeleton, mobileSidebarOpen]);

  // Sprint TENANT-NAV-1 — Main-section admin modal.
  const canManageMainNav = permissions.includes('navigation.manage.org');
  const [mainNavModalOpen, setMainNavModalOpen] = useState(false);

  // Close mobile sidebar on route change
  useEffect(() => {
    if (isMobile) setMobileSidebarOpen(false);
  }, [location.pathname, isMobile, setMobileSidebarOpen]);

  // Client-side permission filter (defence-in-depth — server already filters)
  const visibleItems = useMemo(
    () =>
      items.filter(
        (item) => !item.required_permission || permissions.includes(item.required_permission),
      ),
    [items, permissions],
  );

  const mainItems = useMemo(
    () => visibleItems.filter((i) => i.section === 'main'),
    [visibleItems],
  );
  const adminItems = useMemo(
    () => visibleItems.filter((i) => i.section === 'admin'),
    [visibleItems],
  );
  // Sprint NAV-2 — group the admin items by `admin_group`.
  const adminGroups = useMemo(
    () => computeAdminGroups(adminItems),
    [adminItems],
  );
  const moduleSections = useMemo(() => {
    const sections = computeModuleSections(visibleItems);
    // Sprint CM-17-W2 — CM's manifest has no nav rows post-cutover
    // (management lives entirely in the PageSettingsModal). Ensure a
    // synthetic CONTENT MANAGEMENT section exists when there are
    // intranet root pages to show, so the tree still renders.
    const hasCm = sections.some((s) => s.name === CM_MODULE_NAME);
    if (!hasCm && intranetSites.length > 0) {
      // Sprint NAV-1 — CM sits between Connected Workspace (10s) and
      // Document Management (30s) per the locked owner-facing module
      // priority order.
      sections.push({ name: CM_MODULE_NAME, rank: 20, items: [] });
      sections.sort((a, b) => a.rank - b.rank || a.name.localeCompare(b.name));
    }
    return sections;
  }, [visibleItems, intranetSites]);

  // Sprint NAV-2 — seed default-expanded admin sub-groups on first
  // visit. If the persisted Set has no `admin:` keys, the user has
  // never interacted with admin sub-groups; pre-populate the locked
  // defaults so Identity is open without forcing them to click.
  useEffect(() => {
    setExpandedSections((prev) => {
      const hasAdminKeys = Array.from(prev).some((k) => k.startsWith('admin:'));
      if (hasAdminKeys) return prev;
      const next = new Set(prev);
      for (const g of ADMIN_GROUP_DEFAULT_EXPANDED) next.add(`admin:${g}`);
      writeSectionExpanded(next);
      return next;
    });
    // Run once on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-expand the section containing the active route. Only adds —
  // never removes — so user-driven collapses stick.
  useEffect(() => {
    const path = location.pathname;
    const toExpand: string[] = [];
    if (path.startsWith('/admin')) {
      toExpand.push(ADMIN_SECTION_KEY);
      // Sprint NAV-2 — also open the sub-group containing the active
      // admin path so the user lands looking at the right entry.
      for (const group of adminGroups) {
        if (group.items.some((it) => path.startsWith(it.path))) {
          toExpand.push(`admin:${group.name}`);
        }
      }
    }
    for (const sec of moduleSections) {
      if (sec.items.some((it) => path.startsWith(it.path))) {
        toExpand.push(sec.name);
      }
    }
    if (toExpand.length === 0) return;
    setExpandedSections((prev) => {
      const next = new Set(prev);
      let changed = false;
      for (const k of toExpand) {
        if (!next.has(k)) {
          next.add(k);
          changed = true;
        }
      }
      if (!changed) return prev;
      writeSectionExpanded(next);
      return next;
    });
  }, [location.pathname, moduleSections, adminGroups]);

  const isOpen = isMobile ? mobileSidebarOpen : true;
  const isCollapsed = isMobile ? false : sidebarCollapsed;

  /** Render the intranet-sites tree (used as the CM section's prefix). */
  function renderIntranetSites(collapsed: boolean) {
    if (intranetSites.length === 0) return null;
    const ordered = ((): typeof intranetSites => {
      if (!siteDragOrder) return intranetSites;
      const byId = new Map(intranetSites.map((s) => [s.id, s]));
      const inOrder = siteDragOrder
        .map((id) => byId.get(id))
        .filter((s): s is (typeof intranetSites)[number] => Boolean(s));
      const ids = new Set(siteDragOrder);
      const leftover = intranetSites.filter((s) => !ids.has(s.id));
      return [...inOrder, ...leftover];
    })();

    return (
      <>
        {ordered.map((s, _idx, currentList) => {
          const isExpanded = expandedSites.has(s.id);
          const isDraggingSite = siteDraggingId === s.id;
          const dragHandlers = {
            draggable: !collapsed,
            onDragStart: (e: React.DragEvent) => {
              e.dataTransfer.effectAllowed = 'move';
              e.dataTransfer.setData('text/plain', s.id);
              setSiteDraggingId(s.id);
              if (siteDragOrder === null) {
                setSiteDragOrder(currentList.map((x) => x.id));
              }
            },
            onDragOver: (e: React.DragEvent) => {
              if (!siteDraggingId || siteDraggingId === s.id) return;
              e.preventDefault();
              e.dataTransfer.dropEffect = 'move';
              const order = siteDragOrder ?? currentList.map((x) => x.id);
              const from = order.indexOf(siteDraggingId);
              const to = order.indexOf(s.id);
              if (from < 0 || to < 0 || from === to) return;
              const next = [...order];
              next.splice(from, 1);
              next.splice(to, 0, siteDraggingId);
              setSiteDragOrder(next);
            },
            onDragEnd: async () => {
              const order = siteDragOrder ?? currentList.map((x) => x.id);
              setSiteDraggingId(null);
              try {
                await apiFetch('/mod/cm/pages/roots/reorder', {
                  method: 'POST',
                  body: JSON.stringify({ ordered_page_ids: order }),
                });
              } catch {
                /* tolerate; refresh on next page */
              } finally {
                setSiteDragOrder(null);
              }
            },
          };

          // Sprint CM-12 — external sites render as a single anchor.
          if (s.external_url) {
            return (
              <a
                key={s.id}
                {...dragHandlers}
                href={s.external_url}
                target="_blank"
                rel="noopener noreferrer"
                className={`sidebar__link sidebar__site-link${isDraggingSite ? ' is-dragging' : ''}`}
                title={collapsed ? s.name : s.external_url}
              >
                <ExternalSiteIcon faviconUrl={s.external_favicon_url} size={18} />
                {!collapsed && (
                  <>
                    <span style={{ flex: 1 }}>{s.name}</span>
                    <ExternalLink size={12} />
                  </>
                )}
              </a>
            );
          }
          return (
            <div
              key={s.id}
              {...dragHandlers}
              className={`sidebar__site-group${isDraggingSite ? ' is-dragging' : ''}`}
            >
              <div className="sidebar__site-row">
                {!collapsed && (
                  <button
                    className="sidebar__tree-toggle sidebar__tree-toggle--site"
                    onClick={() => toggleSiteExpand(s.id)}
                    aria-label={isExpanded ? 'Collapse site' : 'Expand site'}
                    title={isExpanded ? 'Collapse' : 'Expand'}
                  >
                    {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </button>
                )}
                <NavLink
                  to={`/modules/cm/${s.slug}`}
                  className={({ isActive }) =>
                    `sidebar__link sidebar__site-link${isActive ? ' sidebar__link--active' : ''}`
                  }
                  title={collapsed ? s.name : undefined}
                >
                  <Globe size={18} />
                  {!collapsed && <span>{s.name}</span>}
                </NavLink>
                {!collapsed && canManageCm && (
                  <button
                    className="sidebar__row-gear"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      openCmModalSettings(s.id);
                    }}
                    title="Page settings"
                    aria-label={`Settings for ${s.name}`}
                  >
                    <Settings size={11} />
                  </button>
                )}
              </div>
              {!collapsed && isExpanded && (
                <div className="sidebar__site-tree">
                  <SiteNavTree
                    siteId={s.id}
                    siteSlug={s.slug}
                    landingPageId={s.landing_page_id}
                  />
                </div>
              )}
            </div>
          );
        })}
      </>
    );
  }

  /** One module's top-level collapsible section. */
  function renderModuleSection(sec: ModuleSection) {
    const isOpen = expandedSections.has(sec.name);
    const isCM = sec.name === CM_MODULE_NAME;
    const showSites = isCM && intranetSites.length > 0;
    const hasChildren = sec.items.length > 0 || showSites;
    if (!hasChildren) return null;

    // Sprint NAV-3 — split items into loose + sub-grouped buckets.
    const { loose, subGroups } = computeModuleLayout(sec.items);

    const renderItem = (item: NavItem) => {
      const Icon = resolveIcon(item.icon);
      return (
        <NavLink
          key={item.path}
          to={item.path}
          end={true}
          className={({ isActive }) =>
            `sidebar__link${isActive ? ' sidebar__link--active' : ''}`
          }
          title={isCollapsed ? item.label : undefined}
        >
          <Icon size={18} />
          {!isCollapsed && <span>{item.label}</span>}
        </NavLink>
      );
    };

    return (
      <div key={sec.name} className="sidebar__section">
        {!isCollapsed && (
          <div className="sidebar__section-header-row">
            <button
              className={`sidebar__section-toggle${isOpen ? ' sidebar__section-toggle--open' : ''}`}
              onClick={() => toggleSection(sec.name)}
              aria-expanded={isOpen}
            >
              <span>{sec.name}</span>
              <ChevronDown size={14} className="sidebar__toggle-icon" />
            </button>
            {isCM && canManageCm && (
              <button
                className="sidebar__section-gear"
                onClick={(e) => {
                  e.stopPropagation();
                  openCmModalPages();
                }}
                title="Manage pages"
                aria-label="Manage Content Management pages"
              >
                <Settings size={13} />
              </button>
            )}
          </div>
        )}
        {(isOpen || isCollapsed) && (
          <>
            {showSites && renderIntranetSites(isCollapsed)}
            {showSites && sec.items.length > 0 && !isCollapsed && (
              <div className="sidebar__section-divider" aria-hidden="true" />
            )}
            {/* Loose items first */}
            {loose.map(renderItem)}
            {/* Sub-group buckets — each is its own collapsible nested
                inside the module section. When the sidebar is collapsed
                (icon-only), flatten everything to a single icon list.
                Sub-groups default to expanded; toggle state shares the
                same expandedSections store under "module:<sec>:sub:<name>". */}
            {!isCollapsed && subGroups.map((group) => {
              // Sub-groups default to open. Storage stores a
              // "closed" sentinel — presence in expandedSections
              // means the user closed it; absence = default-open.
              // Inverted vs the section toggle keeps the default
              // expand without backfilling state on first visit.
              const closedKey = `module:${sec.name}:sub:${group.name}:closed`;
              const isGroupOpen = !expandedSections.has(closedKey);
              return (
                <div key={closedKey} className="sidebar__module-subgroup">
                  <button
                    className={`sidebar__module-subgroup-toggle${isGroupOpen ? ' sidebar__module-subgroup-toggle--open' : ''}`}
                    onClick={() => toggleSection(closedKey)}
                    aria-expanded={isGroupOpen}
                  >
                    <ChevronDown
                      size={12}
                      className="sidebar__toggle-icon"
                    />
                    <span>{group.name}</span>
                  </button>
                  {isGroupOpen && group.items.map(renderItem)}
                </div>
              );
            })}
            {/* Collapsed sidebar: flatten everything into icon-only
                buttons so sub-groups don't render their headers. */}
            {isCollapsed && subGroups.flatMap((g) => g.items).map(renderItem)}
          </>
        )}
      </div>
    );
  }

  const sidebarClasses = [
    'sidebar',
    isCollapsed ? 'sidebar--collapsed' : '',
    isMobile ? 'sidebar--mobile' : '',
    isMobile && isOpen ? 'sidebar--mobile-open' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const adminOpen = expandedSections.has(ADMIN_SECTION_KEY);

  return (
    <>
      {/* Mobile backdrop */}
      {isMobile && isOpen && (
        <div
          className="sidebar-backdrop"
          onClick={() => setMobileSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      <aside className={sidebarClasses} aria-label="Main navigation">
        <div className="sidebar__brand">
          <span className="sidebar__logo">V</span>
          {!isCollapsed && <span className="sidebar__title">vortex_intra</span>}
        </div>

        <nav
          ref={navRef}
          className="sidebar__nav"
          onScroll={(e) => rememberNavScroll(e.currentTarget)}
        >
          {showSkeleton ? (
            <NavSkeleton />
          ) : (
            <>
              {/* MAIN — always-on, no collapse header.
                  Sprint TENANT-NAV-1: section label gets a gear for
                  navigation.manage.org holders; external-URL items
                  render as <a target="_blank">. */}
              {mainItems.length > 0 && (
                <div className="sidebar__section">
                  {!isCollapsed && (
                    <div className="sidebar__section-header-row">
                      <span className="sidebar__section-label">Main</span>
                      {canManageMainNav && (
                        <button
                          className="sidebar__section-gear"
                          onClick={(e) => {
                            e.stopPropagation();
                            setMainNavModalOpen(true);
                          }}
                          title="Manage Main navigation"
                          aria-label="Manage Main navigation"
                        >
                          <Settings size={13} />
                        </button>
                      )}
                    </div>
                  )}
                  {mainItems.map((item) => {
                    const Icon = resolveIcon(item.icon);
                    const isExternal = /^https?:\/\//i.test(item.path);
                    if (isExternal) {
                      return (
                        <a
                          key={item.path}
                          href={item.path}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="sidebar__link"
                          title={isCollapsed ? item.label : item.path}
                        >
                          <Icon size={18} />
                          {!isCollapsed && (
                            <>
                              <span style={{ flex: 1 }}>{item.label}</span>
                              <ExternalLink size={12} style={{ opacity: 0.6 }} />
                            </>
                          )}
                        </a>
                      );
                    }
                    return (
                      <NavLink
                        key={item.path}
                        to={item.path}
                        end={true}
                        className={({ isActive }) =>
                          `sidebar__link${isActive ? ' sidebar__link--active' : ''}`
                        }
                        title={isCollapsed ? item.label : undefined}
                      >
                        <Icon size={18} />
                        {!isCollapsed && <span>{item.label}</span>}
                      </NavLink>
                    );
                  })}
                </div>
              )}

              {/* One top-level section per module, in rank order */}
              {moduleSections.map(renderModuleSection)}

              {/* ADMINISTRATION — Sprint NAV-2: grouped sub-sections */}
              {adminItems.length > 0 && (
                <div className="sidebar__section">
                  {!isCollapsed && (
                    <button
                      className={`sidebar__section-toggle${adminOpen ? ' sidebar__section-toggle--open' : ''}`}
                      onClick={() => toggleSection(ADMIN_SECTION_KEY)}
                      aria-expanded={adminOpen}
                    >
                      <span>Administration</span>
                      <ChevronDown size={14} className="sidebar__toggle-icon" />
                    </button>
                  )}
                  {(adminOpen || isCollapsed) && (
                    isCollapsed
                      // Collapsed sidebar — flatten groups, just render icons.
                      ? adminItems.map((item) => {
                          const Icon = resolveIcon(item.icon);
                          return (
                            <NavLink
                              key={item.path}
                              to={item.path}
                              end={true}
                              className={({ isActive }) =>
                                `sidebar__link${isActive ? ' sidebar__link--active' : ''}`
                              }
                              title={item.label}
                            >
                              <Icon size={18} />
                            </NavLink>
                          );
                        })
                      // Expanded sidebar — render one collapsible
                      // sub-section per admin_group.
                      : adminGroups.map((group) => {
                          const key = `admin:${group.name}`;
                          const isOpenGroup = expandedSections.has(key);
                          return (
                            <div key={key} className="sidebar__admin-group">
                              <button
                                className={`sidebar__admin-group-toggle${isOpenGroup ? ' sidebar__admin-group-toggle--open' : ''}`}
                                onClick={() => toggleSection(key)}
                                aria-expanded={isOpenGroup}
                              >
                                <span>{group.name}</span>
                                <ChevronDown
                                  size={12}
                                  className="sidebar__toggle-icon"
                                />
                              </button>
                              {isOpenGroup && group.items.map((item) => {
                                const Icon = resolveIcon(item.icon);
                                return (
                                  <NavLink
                                    key={item.path}
                                    to={item.path}
                                    end={true}
                                    className={({ isActive }) =>
                                      `sidebar__link${isActive ? ' sidebar__link--active' : ''}`
                                    }
                                  >
                                    <Icon size={18} />
                                    <span>{item.label}</span>
                                  </NavLink>
                                );
                              })}
                            </div>
                          );
                        })
                  )}
                </div>
              )}
            </>
          )}

          {/* Error indicator — shown even when fallback items are displayed */}
          {error && !loading && (
            <div className="sidebar__error" role="alert">
              <span>Navigation unavailable</span>
            </div>
          )}
        </nav>
      </aside>

      {/* CM-17-W2 — PageSettingsModal driven by the gear buttons. */}
      <Suspense fallback={null}>
        {cmModal.open && (
          <PageSettingsModal
            open={cmModal.open}
            initialPageId={cmModal.initialPageId}
            initialTab={cmModal.initialTab}
            onClose={closeCmModal}
          />
        )}
      </Suspense>

      {/* Sprint TENANT-NAV-1 — Main-section curation modal. */}
      <Suspense fallback={null}>
        {mainNavModalOpen && (
          <MainNavSettingsModal
            open={mainNavModalOpen}
            onClose={() => setMainNavModalOpen(false)}
            onChanged={() => refetch()}
          />
        )}
      </Suspense>
    </>
  );
}
